import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { CALENDAR_VIEWS } from "../const";
import { getCompanyToday } from "../../utils/jobOverlapsRange";

export const useCalendarNavigation = (calendarRef, timeZone?: string) => {
  const [currentWeek, setCurrentWeek] = useState(() => dayjs(getCompanyToday(timeZone)));
  const [viewType, setViewType] = useState(CALENDAR_VIEWS.MONTH);

  const syncFCDate = useCallback(
    (newDate: dayjs.Dayjs) => {
      const api = calendarRef.current?.getApi();
      if (api) api.gotoDate(newDate.toDate());
    },
    [calendarRef]
  );

  const handlePrev = useCallback(() => {
    setCurrentWeek((prev) => {
      let next: dayjs.Dayjs;

      switch (viewType) {
        case CALENDAR_VIEWS.DAY:
          next = prev.subtract(1, "day");
          break;
        case CALENDAR_VIEWS.MONTH:
          next = prev.subtract(1, "month");
          break;
        case CALENDAR_VIEWS.LIST:
          next = prev.subtract(1, "week");
          break;
        default:
          next = prev.subtract(1, "day");
      }

      syncFCDate(next);
      return next;
    });
  }, [viewType, syncFCDate]);

  const handleNext = useCallback(() => {
    setCurrentWeek((prev) => {
      let next: dayjs.Dayjs;

      switch (viewType) {
        case CALENDAR_VIEWS.DAY:
          next = prev.add(1, "day");
          break;
        case CALENDAR_VIEWS.MONTH:
          next = prev.add(1, "month");
          break;
        case CALENDAR_VIEWS.LIST:
          next = prev.add(1, "week");
          break;
        default:
          next = prev.add(1, "day");
      }

      syncFCDate(next);
      return next;
    });
  }, [viewType, syncFCDate]);

  const handleToday = useCallback(() => {
    const today = dayjs(getCompanyToday(timeZone));
    setCurrentWeek(today);
    syncFCDate(today);
  }, [syncFCDate, timeZone]);

  const handleViewChange = useCallback(
    (newViewType: string) => {
      setViewType(newViewType);

      const api = calendarRef.current?.getApi();
      if (api) {
        api.changeView(newViewType);
        api.gotoDate(currentWeek.toDate());
      }
    },
    [calendarRef, currentWeek]
  );

  return {
    currentWeek,
    viewType,
    handlePrev,
    handleNext,
    handleToday,
    handleViewChange,
  };
};
